import { useCallback, useEffect, useState } from 'react';
import {
  adminCreateAnnouncement,
  adminDeletePost,
  adminHidePost,
  adminLockPost,
  adminPinPost,
  adminPosts,
  type PostItem,
} from '@suangua/shared';
import { MarkdownView } from '../../../components/board/MarkdownView';
import { EmptyRow, LoadingRow, PageHeader, Pagination, StatusBadge, TableCard, useAdminGuard, useFromNow } from '../parts';

const PAGE_SIZE = 20;
const BOARD_LABEL: Record<string, string> = { chat: '闲聊', divination: '占卜', qa: '问答', announcement: '公告' };
const VERDICT_LABEL: Record<string, string> = { accurate: '灵', inaccurate: '不灵', uncertain: '不好说' };

type BoardFilter = '' | 'chat' | 'divination' | 'qa' | 'announcement';

/**
 * 管理端 · 帖子管理：板块 / 置顶 / 隐藏筛选 + 分页
 * 操作：发布公告 / 置顶切换 / 只读锁定 / 隐藏（公众不可见）/ 删除
 */
export function PostsPage() {
  const guard = useAdminGuard();
  const fromNow = useFromNow();
  const [items, setItems] = useState<PostItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [board, setBoard] = useState<BoardFilter>('');
  const [pinned, setPinned] = useState<'' | '1'>('');
  const [hidden, setHidden] = useState<'' | '1'>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tip, setTip] = useState('');
  const [announceOpen, setAnnounceOpen] = useState(false);

  const load = useCallback(
    async (p = page) => {
      setLoading(true);
      setError('');
      try {
        const res = await adminPosts(board, pinned, p, PAGE_SIZE, hidden);
        setItems(res.items);
        setTotal(res.total);
        setPage(res.page);
      } catch (e) {
        setError(guard(e));
      } finally {
        setLoading(false);
      }
    },
    [guard, board, pinned, hidden, page],
  );

  // 筛选变化 → 回第一页
  useEffect(() => {
    void load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board, pinned, hidden]);

  async function togglePin(p: PostItem) {
    setError('');
    setTip('');
    try {
      await adminPinPost(p.id, p.is_pinned === 1 ? 0 : 1);
      setTip(p.is_pinned === 1 ? `已取消置顶 #${p.id}` : `已置顶 #${p.id}`);
      await load(page);
    } catch (e) {
      setError(guard(e));
    }
  }

  /** 只读锁定/解锁：锁定后用户端无法再回复 */
  async function toggleLock(p: PostItem) {
    setError('');
    setTip('');
    try {
      await adminLockPost(p.id, p.is_locked === 1 ? 0 : 1);
      setTip(p.is_locked === 1 ? `已解除只读 #${p.id}` : `已设为只读 #${p.id}`);
      await load(page);
    } catch (e) {
      setError(guard(e));
    }
  }

  /** 隐藏/取消隐藏：隐藏后公众不可见（作者与管理端仍可见） */
  async function toggleHidden(p: PostItem) {
    setError('');
    setTip('');
    try {
      await adminHidePost(p.id, p.is_hidden === 1 ? 0 : 1);
      setTip(p.is_hidden === 1 ? `已取消隐藏 #${p.id}` : `已隐藏 #${p.id}（仅作者与管理端可见）`);
      await load(page);
    } catch (e) {
      setError(guard(e));
    }
  }

  async function remove(p: PostItem) {
    if (!window.confirm(`确认删除帖子「${p.title || `占卜贴 #${p.id}`}」？评论与点赞将一并删除。`)) return;
    setError('');
    setTip('');
    try {
      await adminDeletePost(p.id);
      setTip(`已删除 #${p.id}`);
      await load(page);
    } catch (e) {
      setError(guard(e));
    }
  }

  /** 公告发布成功 → 刷新列表并定位到公告板块（同板块时手动刷新，其余交给筛选变化的 effect） */
  async function onAnnounced(id: number) {
    setAnnounceOpen(false);
    setTip(`公告已发布 #${id}`);
    if (board === 'announcement') await load(1);
    else setBoard('announcement');
  }

  return (
    <div>
      <PageHeader title="帖子管理" desc="删除为级联操作（评论 / 点赞一并清除），不可恢复">
        <button
          onClick={() => setAnnounceOpen(true)}
          className="rounded-lg bg-stone-800 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-stone-700"
        >
          <i className="fa-solid fa-bullhorn mr-1" /> 发布公告
        </button>
        <select
          className="rounded-lg border border-stone-200 bg-white px-2.5 py-1.5 text-xs text-stone-600 outline-none focus:border-stone-400"
          value={board}
          onChange={(e) => setBoard(e.target.value as BoardFilter)}
        >
          <option value="">全部板块</option>
          <option value="chat">闲聊</option>
          <option value="divination">占卜</option>
          <option value="qa">问答</option>
          <option value="announcement">公告</option>
        </select>
        <select
          className="rounded-lg border border-stone-200 bg-white px-2.5 py-1.5 text-xs text-stone-600 outline-none focus:border-stone-400"
          value={hidden}
          onChange={(e) => setHidden(e.target.value as typeof hidden)}
        >
          <option value="">全部状态</option>
          <option value="1">仅看隐藏</option>
        </select>
        <select
          className="rounded-lg border border-stone-200 bg-white px-2.5 py-1.5 text-xs text-stone-600 outline-none focus:border-stone-400"
          value={pinned}
          onChange={(e) => setPinned(e.target.value as typeof pinned)}
        >
          <option value="">全部帖子</option>
          <option value="1">仅置顶</option>
        </select>
      </PageHeader>

      {error && <p className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-600">{error}</p>}
      {tip && <p className="mb-3 rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-700">{tip}</p>}

      <TableCard footer={<Pagination page={page} size={PAGE_SIZE} total={total} onPage={(p) => void load(p)} />}>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-stone-100 text-left text-xs text-stone-400">
              <th className="px-4 py-2.5 font-medium">帖子</th>
              <th className="px-4 py-2.5 font-medium">板块</th>
              <th className="px-4 py-2.5 font-medium">数据</th>
              <th className="px-4 py-2.5 font-medium">发布时间</th>
              <th className="px-4 py-2.5 text-right font-medium">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <LoadingRow />
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <EmptyRow text="没有匹配的帖子" />
                </td>
              </tr>
            ) : (
              items.map((p) => (
                <tr key={p.id} className="border-b border-stone-50 last:border-0 hover:bg-stone-50/60">
                  <td className="max-w-0 px-4 py-2.5">
                    <p className="truncate font-medium text-stone-700">
                      {p.is_pinned === 1 && <span className="mr-1 text-xs text-red-500">[顶]</span>}
                      {p.is_locked === 1 && <span className="mr-1 text-xs text-stone-400">[锁]</span>}
                      {p.is_hidden === 1 && <span className="mr-1 text-xs text-amber-600">[隐]</span>}
                      {p.title || `占卜贴 #${p.id}`}
                      {p.verdict && (
                        <span className="ml-1.5 text-[11px] font-normal text-stone-400">（{VERDICT_LABEL[p.verdict]}）</span>
                      )}
                    </p>
                    <p className="truncate text-xs text-stone-400">
                      #{p.id} · {p.username}
                    </p>
                  </td>
                  <td className="px-4 py-2.5">
                    <StatusBadge tone={p.board === 'announcement' ? 'amber' : 'gray'}>
                      {BOARD_LABEL[p.board] ?? p.board}
                    </StatusBadge>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-xs text-stone-400">
                    <i className="fa-regular fa-thumbs-up" /> {p.like_count} · <i className="fa-regular fa-comment" /> {p.reply_count}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-xs text-stone-400">{fromNow(p.created_at)}</td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right text-xs">
                    <button
                      onClick={() => void togglePin(p)}
                      className="rounded-md border border-stone-200 px-2 py-1 text-stone-500 transition-colors hover:bg-stone-100"
                    >
                      {p.is_pinned === 1 ? '取消置顶' : '置顶'}
                    </button>
                    <button
                      onClick={() => void toggleLock(p)}
                      className="ml-1.5 rounded-md border border-stone-200 px-2 py-1 text-stone-500 transition-colors hover:bg-stone-100"
                    >
                      {p.is_locked === 1 ? '解除只读' : '设为只读'}
                    </button>
                    <button
                      onClick={() => void toggleHidden(p)}
                      className="ml-1.5 rounded-md border border-amber-200 px-2 py-1 text-amber-600 transition-colors hover:bg-amber-50"
                    >
                      {p.is_hidden === 1 ? '取消隐藏' : '隐藏'}
                    </button>
                    <button
                      onClick={() => void remove(p)}
                      className="ml-1.5 rounded-md border border-red-200 px-2 py-1 text-red-500 transition-colors hover:bg-red-50"
                    >
                      删除
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </TableCard>

      {announceOpen && (
        <AnnouncementModal
          onClose={() => setAnnounceOpen(false)}
          onPublished={onAnnounced}
          onError={(msg) => setError(msg)}
        />
      )}
    </div>
  );
}

/**
 * 发布公告弹窗：标题 + Markdown 正文（编辑/预览）。
 * 提交到 POST /api/admin/announcements，帖子落在公告板块（作者展示「官方」）。
 */
function AnnouncementModal(props: {
  onClose: () => void;
  onPublished: (id: number) => void | Promise<void>;
  onError: (msg: string) => void;
}) {
  const { onClose, onPublished, onError } = props;
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tab, setTab] = useState<'edit' | 'preview'>('edit');
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = title.trim().length > 0 && content.trim().length > 0 && !submitting;

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await adminCreateAnnouncement(title.trim(), content.trim());
      await onPublished(res.id);
    } catch (e) {
      onError(e instanceof Error ? e.message : '发布公告失败，请稍后再试');
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-stone-900/40" onClick={onClose} />
      <div className="relative flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-stone-100 px-5 py-3.5">
          <h2 className="text-sm font-bold text-stone-800">
            <i className="fa-solid fa-bullhorn mr-1.5 text-amber-500" /> 发布公告
          </h2>
          <button onClick={onClose} className="text-stone-400 transition-colors hover:text-stone-600" aria-label="关闭">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
          <input
            className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm text-stone-700 outline-none focus:border-stone-400"
            placeholder="公告标题（1~80 字）"
            maxLength={80}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <div className="flex items-center justify-between">
            <div className="flex gap-1 rounded-lg bg-stone-100 p-0.5 text-xs">
              <button
                className={`rounded-md px-3 py-1 ${tab === 'edit' ? 'bg-white font-medium text-stone-700 shadow-sm' : 'text-stone-500'}`}
                onClick={() => setTab('edit')}
              >
                编辑
              </button>
              <button
                className={`rounded-md px-3 py-1 ${tab === 'preview' ? 'bg-white font-medium text-stone-700 shadow-sm' : 'text-stone-500'}`}
                onClick={() => setTab('preview')}
              >
                预览
              </button>
            </div>
            <span className="text-[11px] text-stone-400">{content.length} / 50000</span>
          </div>
          {tab === 'edit' ? (
            <textarea
              className="w-full resize-y rounded-lg border border-stone-200 px-3 py-2 text-sm leading-relaxed text-stone-700 outline-none focus:border-stone-400"
              rows={9}
              maxLength={50000}
              placeholder="公告正文，支持 Markdown：# 标题、**加粗**、> 引用、`代码`、![图片|300](链接)"
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
          ) : (
            <div className="min-h-40 rounded-lg border border-stone-200 px-3 py-2">
              {content.trim() ? (
                <MarkdownView content={content} />
              ) : (
                <p className="text-xs text-stone-400">暂无内容</p>
              )}
            </div>
          )}
          <p className="text-[11px] text-stone-400">公告将发布到「公告」板块，作者展示为「官方」，用户可在帖子下方回复。</p>
        </div>

        <div className="flex justify-end gap-2 border-t border-stone-100 px-5 py-3">
          <button
            onClick={onClose}
            className="rounded-lg border border-stone-200 px-4 py-1.5 text-sm text-stone-500 transition-colors hover:bg-stone-50"
          >
            取消
          </button>
          <button
            disabled={!canSubmit}
            onClick={() => void submit()}
            className="rounded-lg bg-stone-800 px-4 py-1.5 text-sm font-medium text-white transition-colors hover:bg-stone-700 disabled:opacity-40"
          >
            {submitting ? '发布中…' : '发布'}
          </button>
        </div>
      </div>
    </div>
  );
}
